/**
 * Negative Keywords Management Component
 * Manages campaign-level negative keywords with:
 * - Display of current negative keywords from app_configuration
 * - AI-powered recommendations
 * - Manual keyword addition
 * - Bulk removal operations
 * - Optimistic updates for instant UI feedback
 */
import { useState, useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ActionButtonGroup, AIRecommendationsButton, AddManuallyButton, Button, getAIButtonStatus } from '@/components/ui';
import { BulkManagementTable } from '@/components/common/BulkManagementTable';
import {
  RecommendationsTable,
  SaveActionBar,
  type TableColumn,
} from '@/components/common/Recommendations';
import { useRecommendationSelection } from '@/hooks/recommendations/useRecommendationSelection';
import { useAuthStore } from '@/store/authStore';
import { debug, toast } from '@/utils';
import { formatEnumPretty } from '@/utils/formatEnum';
import apiClient from '@/services/api/client';
import type { Campaign } from '@/types/campaign';

interface NegativeKeyword {
  criterion_id: string;
  keyword_text: string;
  match_type: string;
  status?: string;
}

interface NegativeKeywordRecommendation {
  keyword: string;
  match_type: string;
  search_term: string;
  cost: number;
  impressions: number;
  clicks: number;
  conversions: number;
  justification: string;
}

interface PerformanceMaxNegativeKeywordsProps {
  campaign: Campaign;
  /** When 'embedded', renders without outer card wrapper for use inside other cards */
  variant?: 'default' | 'embedded';
}

interface RecommendationsResponse {
  campaign_id: string;
  campaign_name: string;
  analysis_period: string;
  total_recommendations: number;
  recommendations: NegativeKeywordRecommendation[];
}

export function PerformanceMaxNegativeKeywords({
  campaign,
  variant = 'default'
}: PerformanceMaxNegativeKeywordsProps) {
  const isEmbedded = variant === 'embedded';
  const queryClient = useQueryClient();
  const { memberUuid } = useAuthStore();
  const [recommendations, setRecommendations] = useState<NegativeKeywordRecommendation[]>([]);
  const { selectedIndices, toggleItem, selectAll, deselectAll, clearSelection } = useRecommendationSelection();
  const [isLoadingRecommendations, setIsLoadingRecommendations] = useState(false);
  const [isAddingKeywords, setIsAddingKeywords] = useState(false);
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [manualKeyword, setManualKeyword] = useState('');
  const [manualMatchType, setManualMatchType] = useState<'BROAD' | 'PHRASE' | 'EXACT'>('BROAD');
  const [error, setError] = useState<string | null>(null);
  const [buttonStatus, setButtonStatus] = useState<string>('');
  const [selectedKeywords, setSelectedKeywords] = useState<Set<string>>(new Set());
  const [analysisPeriod, setAnalysisPeriod] = useState<string>('');
  const [isDebugMode, setIsDebugMode] = useState(debug.isDev());

  // Listen for debug mode changes
  useEffect(() => {
    const updateDebugMode = () => {
      setIsDebugMode(debug.isDev());
    };
    const unsubscribe = debug.onModeChange(updateDebugMode);
    updateDebugMode();
    return unsubscribe;
  }, []);

  // Clear state when campaign changes
  useEffect(() => {
    setRecommendations([]);
    clearSelection();
    setButtonStatus('');
    setSelectedKeywords(new Set());
    setError(null);
  }, [campaign.campaign_id, clearSelection]);

  // Get negative keywords from campaign (already in app_configuration)
  // Filter out REMOVED keywords from database
  const negativeKeywords = useMemo(() => {
    const negativeKeywordsFromConfig = (campaign as Campaign & { negative_keywords?: NegativeKeyword[] }).negative_keywords || [];
    return negativeKeywordsFromConfig
      .filter((kw: NegativeKeyword) => kw.status !== 'REMOVED')
      .map((kw: NegativeKeyword) => kw) as NegativeKeyword[];
  }, [campaign]);

  // Get AI recommendations
  const getRecommendations = async () => {
    setIsLoadingRecommendations(true);
    setError(null);
    setButtonStatus('');
    setRecommendations([]);
    clearSelection();

    try {
      // Pass existing negative keywords to avoid duplicates in recommendations
      const existingKeywords = negativeKeywords.map(kw => ({
        keyword_text: kw.keyword_text,
        match_type: kw.match_type
      }));

      const response = await apiClient.post(
        `/campaigns/${campaign.campaign_id}/negative-keywords/recommendations?days=30`,
        { existing_negative_keywords: existingKeywords },
        { timeout: 120000 }
      );

      const data = response.data as RecommendationsResponse;
      setRecommendations(data.recommendations || []);
      setAnalysisPeriod(data.analysis_period || '');

      // Log what we received including landing page info
      debug.log('🔍 Negative Keyword Recommendations Response:', {
        campaign_id: data.campaign_id,
        campaign_name: data.campaign_name,
        analysis_period: data.analysis_period,
        total_recommendations: data.total_recommendations,
        recommendations: data.recommendations
      });

      // Alert user about landing page usage
      if (data.analysis_period && data.analysis_period.includes('Landing page:')) {
        debug.log('✅ Landing page content WAS used for recommendations');
      } else {
        debug.warn('⚠️ Landing page content was NOT used for recommendations');
      }

      if (!data.recommendations || data.recommendations.length === 0) {
        setButtonStatus('No recommendations found');
      } else {
        setButtonStatus(`Generated ${data.recommendations.length} recommendations`);
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string; message?: string } }; message?: string };
      const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to get recommendations';

      // Landing page access errors should show as toast to avoid layout shift
      if (errorMsg.includes('landing page') || errorMsg.includes('Landing page')) {
        toast.error(errorMsg);
      } else {
        setError(errorMsg);
      }
      debug.error('Error getting recommendations:', error);
    } finally {
      setIsLoadingRecommendations(false);
    }
  };

  // Add negative keywords
  const addNegativeKeywords = async (keywords: Array<{ text: string; match_type: string }>) => {
    if (!memberUuid) {
      setError('Authentication required. Please log in.');
      return;
    }

    setIsAddingKeywords(true);
    setError(null);

    try {
      const response = await apiClient.post('/negative-keywords/add', {
        member_uuid: memberUuid,
        campaign_id: campaign.campaign_id,
        customer_id: (campaign as Campaign & { google_ads_account_id?: string }).google_ads_account_id || undefined, // Include if available
        keywords: keywords,
        trigger_data_sync: true
      });

      const data = response.data;
      debug.log('Added negative keywords:', data);

      // Optimistically update the app-config cache
      const appConfigKeys = queryClient.getQueriesData({ queryKey: ['app-config'] });
      appConfigKeys.forEach(([queryKey, oldData]) => {
        if (oldData && typeof oldData === 'object' && 'configuration' in oldData) {
          const config = oldData as { configuration?: { campaign_list?: Array<{ campaign_id: string; negative_keywords?: NegativeKeyword[] }> } };
          const campaignList = config.configuration?.campaign_list || [];

          // Find and update the campaign with new negative keywords
          const updatedCampaignList = campaignList.map((c: { campaign_id: string; negative_keywords?: NegativeKeyword[] }) => {
            if (c.campaign_id === campaign.campaign_id) {
              const existingKeywords = c.negative_keywords || [];
              const newKeywords = (data.keywords_added as Array<{ criterion_id: string; text: string; match_type: string }>)?.map((kw) => ({
                criterion_id: kw.criterion_id,
                keyword_text: kw.text,
                match_type: kw.match_type,
                status: 'ENABLED'
              })) || [];

              return {
                ...c,
                negative_keywords: [...existingKeywords, ...newKeywords]
              };
            }
            return c;
          });

          queryClient.setQueryData(queryKey, {
            ...config,
            configuration: {
              ...config.configuration,
              campaign_list: updatedCampaignList
            }
          });
        }
      });

      debug.log('Optimistically updated app-config cache with new negative keywords');

      // Also update campaign-from-cache query to trigger component re-render
      const campaignCacheKeys = queryClient.getQueriesData({
        queryKey: ['campaign-from-cache', campaign.campaign_id]
      });
      campaignCacheKeys.forEach(([queryKey, oldCampaign]) => {
        if (oldCampaign && typeof oldCampaign === 'object') {
          const existingKeywords = (oldCampaign as Campaign & { negative_keywords?: NegativeKeyword[] }).negative_keywords || [];
          const newKeywords = (data.keywords_added as Array<{ criterion_id: string; text: string; match_type: string }>)?.map((kw) => ({
            criterion_id: kw.criterion_id,
            keyword_text: kw.text,
            match_type: kw.match_type,
            status: 'ENABLED'
          })) || [];

          queryClient.setQueryData(queryKey, {
            ...oldCampaign,
            negative_keywords: [...existingKeywords, ...newKeywords]
          });
        }
      });

      debug.log('Optimistically updated campaign-from-cache with new negative keywords');

      toast.success(`Added ${data.added_count} negative keyword${data.added_count > 1 ? 's' : ''}`);

      // Clear selections and recommendations if they were added from recommendations
      if (selectedIndices.size > 0) {
        clearSelection();
        setRecommendations([]);
      }

      // Clear manual input
      setManualKeyword('');
      setShowManualAdd(false);

      // Don't invalidate immediately - let optimistic update persist
      // The data sync webhook will update the database in 30 seconds
      // and the next periodic refetch will get the real data

      return data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      const errorMsg = err.response?.data?.message || err.message || 'Failed to add negative keywords';
      setError(errorMsg);
      toast.error(errorMsg);
      debug.error('Error adding negative keywords:', error);
      throw error;
    } finally {
      setIsAddingKeywords(false);
    }
  };

  // Delete negative keywords (supports bulk deletion)
  const deleteNegativeKeywords = async (criterionIds: string[]) => {
    if (!memberUuid) {
      setError('Authentication required. Please log in.');
      return;
    }

    setError(null);

    // Store previous data for rollback on error (declared outside try for catch access)
    const previousData: Array<{ queryKey: readonly unknown[]; oldData: unknown }> = [];

    try {
      // Optimistically update the app-config cache (remove keywords immediately)
      const appConfigKeys = queryClient.getQueriesData({ queryKey: ['app-config'] });

      appConfigKeys.forEach(([queryKey, oldData]) => {
        if (oldData && typeof oldData === 'object' && 'configuration' in oldData) {
          // Store previous data for rollback on error
          previousData.push({ queryKey, oldData });

          const config = oldData as { configuration?: { campaign_list?: Array<{ campaign_id: string; negative_keywords?: NegativeKeyword[] }> } };
          const campaignList = config.configuration?.campaign_list || [];

          // Find and update the campaign, removing the specified negative keywords
          const updatedCampaignList = campaignList.map((c: { campaign_id: string; negative_keywords?: NegativeKeyword[] }) => {
            if (c.campaign_id === campaign.campaign_id) {
              const filteredKeywords = (c.negative_keywords || []).filter(
                (kw: NegativeKeyword) => !criterionIds.includes(kw.criterion_id)
              );

              return {
                ...c,
                negative_keywords: filteredKeywords
              };
            }
            return c;
          });

          queryClient.setQueryData(queryKey, {
            ...config,
            configuration: {
              ...config.configuration,
              campaign_list: updatedCampaignList
            }
          });
        }
      });

      debug.log('Optimistically removed negative keywords from cache');

      // Also update campaign-from-cache query to trigger component re-render
      const campaignCacheKeys = queryClient.getQueriesData({
        queryKey: ['campaign-from-cache', campaign.campaign_id]
      });
      campaignCacheKeys.forEach(([queryKey, oldCampaign]) => {
        if (oldCampaign && typeof oldCampaign === 'object') {
          // Store previous data for rollback on error
          previousData.push({ queryKey, oldData: oldCampaign });

          const filteredKeywords = ((oldCampaign as Campaign & { negative_keywords?: NegativeKeyword[] }).negative_keywords || []).filter(
            (kw: NegativeKeyword) => !criterionIds.includes(kw.criterion_id)
          );

          queryClient.setQueryData(queryKey, {
            ...oldCampaign,
            negative_keywords: filteredKeywords
          });
        }
      });

      debug.log('Optimistically updated campaign-from-cache, removed negative keywords');

      // Call the backend API
      debug.log('Campaign object:', { campaign_id: campaign.campaign_id, google_ads_account_id: (campaign as Campaign & { google_ads_account_id?: string }).google_ads_account_id });

      const requestBody = {
        member_uuid: memberUuid,
        campaign_id: campaign.campaign_id,
        customer_id: (campaign as Campaign & { google_ads_account_id?: string }).google_ads_account_id || undefined, // Include if available
        criterion_ids: criterionIds,
        trigger_data_sync: true
      };

      debug.log('Removing negative keywords:', requestBody);

      const response = await apiClient.post('/negative-keywords/remove', requestBody);

      const data = response.data;
      debug.log('Removed negative keywords:', data);

      toast.success(`Removed ${data.successful_removals} negative keyword${data.successful_removals > 1 ? 's' : ''}`);

      // Clear selections
      setSelectedKeywords(new Set());

      // Don't invalidate immediately - let optimistic update persist
      // The data sync webhook will update the database in 30 seconds
      // and the next periodic refetch will get the real data
    } catch (error: unknown) {
      // Rollback optimistic update on error
      previousData.forEach(({ queryKey, oldData }) => {
        queryClient.setQueryData(queryKey, oldData);
      });
      debug.log('Rolled back optimistic update due to error');

      const err = error as { response?: { data?: { message?: string; detail?: string } }; message?: string };
      const errorMsg = err.response?.data?.message || err.response?.data?.detail || err.message || 'Failed to delete negative keywords';
      setError(errorMsg);
      toast.error(errorMsg);
      debug.error('Error deleting negative keywords:', error);
    }
  };

  // Add selected recommendations
  const handleAddSelectedRecommendations = async () => {
    const keywordsToAdd = Array.from(selectedIndices).map(index => {
      const rec = recommendations[index];
      return {
        text: rec.keyword,
        match_type: rec.match_type,
      };
    });

    // addNegativeKeywords clears selections/recommendations when selectedIndices.size > 0
    await addNegativeKeywords(keywordsToAdd);
  };

  // Add manual keyword
  const handleAddManualKeyword = async () => {
    if (!manualKeyword.trim()) {
      setError('Please enter a keyword');
      return;
    }

    await addNegativeKeywords([{
      text: manualKeyword.trim(),
      match_type: manualMatchType,
    }]);
  };

  // Handle keyword selection toggle
  const handleKeywordToggle = (criterionId: string) => {
    setSelectedKeywords(prev => {
      const next = new Set(prev);
      if (next.has(criterionId)) {
        next.delete(criterionId);
      } else {
        next.add(criterionId);
      }
      return next;
    });
  };

  // Handle select all keywords
  const handleSelectAllKeywords = () => {
    if (selectedKeywords.size === negativeKeywords.length) {
      setSelectedKeywords(new Set());
    } else {
      setSelectedKeywords(new Set(negativeKeywords.map(k => k.criterion_id)));
    }
  };

  // Execute batch action - matches BulkManagementTable signature
  const handleExecuteBatchAction = async (action: 'enable' | 'pause' | 'remove', itemIds: string[]) => {
    if (action === 'remove') {
      await deleteNegativeKeywords(itemIds);
    }
  };

  // Table columns for recommendations
  const recommendationColumns: TableColumn<NegativeKeywordRecommendation>[] = [
    {
      header: 'Keyword',
      key: 'keyword',
      render: (rec) => (
        <span className="text-sm font-medium text-gray-900">{rec.keyword}</span>
      ),
    },
    {
      header: 'Match Type',
      key: 'match_type',
      render: (rec) => (
        <span className="px-2 py-1 rounded-md bg-gray-100 text-xs font-medium">
          {formatEnumPretty(rec.match_type)}
        </span>
      ),
    },
    {
      header: 'Search Term',
      key: 'search_term',
      render: (rec) => (
        <span className="text-sm text-gray-600">{rec.search_term}</span>
      ),
    },
    {
      header: 'Performance',
      key: 'performance',
      render: (rec) => (
        <div className="text-xs text-gray-600">
          <div>Cost: ${rec.cost.toFixed(2)}</div>
          <div>Clicks: {rec.clicks}</div>
          <div>Conv: {rec.conversions.toFixed(1)}</div>
        </div>
      ),
    },
    {
      header: 'Justification',
      key: 'justification',
      minWidth: '200px',
      render: (rec) => (
        <p className="text-xs text-gray-600">{rec.justification}</p>
      ),
    },
  ];

  return (
    <div className={isEmbedded ? '' : 'mt-6 flex justify-center'}>
      <div className={isEmbedded ? 'p-4' : 'bg-white p-6 rounded-lg shadow border max-w-4xl w-full'}>
        <div className="flex items-center justify-between mb-4">
          <h2 className={isEmbedded ? 'text-base font-semibold text-gray-900' : 'text-xl font-semibold text-gray-900'}>
            Manage Negative Keyword Exclusions
          </h2>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Add negative keywords to prevent your ads from showing for irrelevant searches.
        </p>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Action Buttons */}
        <ActionButtonGroup
          statusMessage={getAIButtonStatus(isLoadingRecommendations, 'negative keywords', buttonStatus) || undefined}
          isLoading={isLoadingRecommendations}
          className="mb-4"
        >
          <AIRecommendationsButton
            onClick={getRecommendations}
            disabled={isLoadingRecommendations}
            isLoading={isLoadingRecommendations}
          />
          <AddManuallyButton
            onClick={() => setShowManualAdd(!showManualAdd)}
          />
        </ActionButtonGroup>

        {/* Manual Add Form */}
        {showManualAdd && (
          <div className="mb-4 p-4 border rounded-lg bg-gray-50">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Add Negative Keyword</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label htmlFor="manual-keyword" className="block text-sm font-medium text-gray-700 mb-1">
                  Keyword Text
                </label>
                <input
                  id="manual-keyword"
                  type="text"
                  value={manualKeyword}
                  onChange={(e) => setManualKeyword(e.target.value)}
                  placeholder="e.g., free, cheap, discount"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              <div>
                <label htmlFor="manual-match-type" className="block text-sm font-medium text-gray-700 mb-1">
                  Match Type
                </label>
                <select
                  id="manual-match-type"
                  value={manualMatchType}
                  onChange={(e) => setManualMatchType(e.target.value as 'BROAD' | 'PHRASE' | 'EXACT')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="BROAD">Broad</option>
                  <option value="PHRASE">Phrase</option>
                  <option value="EXACT">Exact</option>
                </select>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={handleAddManualKeyword}
                disabled={!manualKeyword.trim() || isAddingKeywords}
              >
                {isAddingKeywords ? 'Adding...' : 'Add Keyword'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setShowManualAdd(false);
                  setManualKeyword('');
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Current Negative Keywords */}
        <div className="mb-6">
          <h3 className={`${isEmbedded ? 'text-sm' : 'text-lg'} font-medium text-gray-900 mb-3`}>Current Negative Keywords ({negativeKeywords.length})</h3>

          {negativeKeywords.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-gray-600">No negative keywords added yet</p>
              <p className="text-sm text-gray-500 mt-1">Use AI recommendations or add keywords manually</p>
            </div>
          ) : (
            <BulkManagementTable
              items={negativeKeywords.map(keyword => ({
                id: keyword.criterion_id,
                text: keyword.keyword_text,
                match_type: keyword.match_type,
                status: (keyword.status as 'ENABLED' | 'PAUSED' | 'REMOVED') || 'ENABLED',
              }))}
              selectedItems={selectedKeywords}
              onToggleItem={handleKeywordToggle}
              onSelectAll={handleSelectAllKeywords}
              onBulkAction={handleExecuteBatchAction}
              itemLabel="negative keyword"
              columns={[
                {
                  header: 'Keyword',
                  key: 'text',
                  render: (item) => <span className="text-sm text-gray-900">{item.text}</span>
                },
                {
                  header: 'Match Type',
                  key: 'match_type',
                  render: (item) => (
                    <span className="px-2 py-1 rounded-md bg-gray-100 text-xs font-medium">
                      {formatEnumPretty(item.match_type)}
                    </span>
                  )
                }
              ]}
              showStatus={true}
              allowedActions={['remove']}
            />
          )}
        </div>

        {/* AI Recommendations */}
        {recommendations.length > 0 && (
          <div className="mb-6">
            {isDebugMode && analysisPeriod && (
              <p className="text-xs text-gray-500 mb-4 font-mono bg-gray-50 p-2 rounded">
                📊 Analysis: {analysisPeriod}
              </p>
            )}

            <RecommendationsTable
              items={recommendations}
              columns={recommendationColumns}
              selectedItems={selectedIndices}
              onToggleItem={toggleItem}
              onSelectAll={() => selectAll(recommendations.length)}
              onDeselectAll={deselectAll}
              itemCount={recommendations.length}
              itemLabel="Negative Keywords"
            />
            <SaveActionBar
              selectedCount={selectedIndices.size}
              itemLabel="negative keyword"
              onSave={handleAddSelectedRecommendations}
              isSaving={isAddingKeywords}
              disabled={selectedIndices.size === 0}
            />
          </div>
        )}

      </div>
    </div>
  );
}
